import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const KVSelectWithPreview = ({
  className,
  options = [],
  value,
  placeholder = "Select replacement KV...",
  disabled = false,
  error = false,
  onChange,
  getBrandName,
  showSelectedPreview = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef(null);

  // Get selected option for display
  const selectedOption = options?.find(opt => opt?.value === value);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionSelect = (option) => {
    onChange?.(option?.value);
    setIsOpen(false);
    setHoveredOption(null);
  };

  const handleMouseEnter = (option, event) => {
    setHoveredOption(option);
    const rect = event?.target?.getBoundingClientRect();
    setTooltipPosition({
      x: rect?.left + rect?.width + 10,
      y: rect?.top + (rect?.height / 2)
    });
  };

  const handleMouseLeave = () => {
    setHoveredOption(null);
  };

  const ThumbnailPreview = ({ kv, size = 'small', className = '' }) => {
    if (!kv?.thumbnailUrl) {
      return (
        <div className={cn(
          "bg-gray-100 border border-gray-200 rounded flex items-center justify-center text-gray-400",
          size === 'small' ? 'w-12 h-8' : 'w-24 h-16',
          className
        )}>
          <Icon name="ImageOff" size={size === 'small' ? 12 : 16} />
          <span className="text-xs ml-1 hidden sm:inline">No Preview</span>
        </div>
      );
    }

    return (
      <div className={cn(
        "relative rounded overflow-hidden border border-gray-200 bg-gray-50",
        size === 'small' ? 'w-12 h-8' : 'w-24 h-16',
        className
      )}>
        <img 
          src={kv?.thumbnailUrl}
          alt={kv?.title || 'KV Preview'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {kv?.fileType === 'video' && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <div className="bg-white/90 rounded-full p-1">
              <Icon name="Play" size={size === 'small' ? 8 : 12} className="text-gray-700" />
            </div>
          </div>
        )}
        {kv?.fileType === 'video' && kv?.durationDisplay && (
          <div className="absolute bottom-0 right-0 bg-black/70 text-white text-xs px-1 rounded-tl">
            {kv?.durationDisplay}
          </div>
        )}
      </div>
    );
  };

  const LargePreviewTooltip = ({ kv, position }) => {
    if (!kv) return null;

    return (
      <div 
        className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl p-3 max-w-sm"
        style={{
          left: `${position?.x}px`,
          top: `${position?.y - 150}px`,
          transform: 'translateY(-50%)'
        }}
      >
        <div className="space-y-2">
          <div className="w-72 h-48 rounded overflow-hidden bg-gray-100">
            {kv?.thumbnailUrl ? (
              <div className="relative w-full h-full">
                <img 
                  src={kv?.thumbnailUrl}
                  alt={kv?.title}
                  className="w-full h-full object-cover"
                />
                {kv?.fileType === 'video' && (
                  <>
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-2">
                        <Icon name="Play" size={20} className="text-gray-700" />
                      </div>
                    </div>
                    {kv?.durationDisplay && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-sm px-2 py-1 rounded">
                        Duration: {kv?.durationDisplay}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Icon name="ImageOff" size={32} className="mx-auto mb-2" />
                  <p className="text-sm">No Preview Available</p>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <h4 className="font-medium text-sm text-foreground">{kv?.kvId}</h4>
            <p className="text-xs text-muted-foreground">{kv?.title}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{getBrandName?.(kv?.brandId)}</span>
              <span>•</span>
              <span>{kv?.brandingType}</span>
              <span>•</span>
              <span>{kv?.dimensions}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={cn("relative", className)}>
        <button
          ref={dropdownRef}
          type="button"
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus:ring-destructive",
            !selectedOption && "text-muted-foreground"
          )}
          onClick={handleToggle}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedOption ? (
              <>
                {showSelectedPreview && (
                  <ThumbnailPreview kv={selectedOption} size="small" className="flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <span className="truncate font-medium">
                    {selectedOption?.kvId}
                  </span>
                  <span className="text-muted-foreground ml-2 truncate">
                    {getBrandName?.(selectedOption?.brandId)}
                  </span>
                </div>
              </>
            ) : (
              <span className="truncate">{placeholder}</span>
            )}
          </div>

          <Icon 
            name="ChevronDown" 
            size={16} 
            className={cn("transition-transform ml-2 flex-shrink-0", isOpen && "rotate-180")} 
          />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white text-black border border-border rounded-md shadow-lg max-h-60 overflow-auto">
            <div className="py-1">
              {options?.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No KVs available
                </div>
              ) : (
                options?.map((option) => (
                  <div
                    key={option?.value}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                      value === option?.value && "bg-primary/10 text-primary",
                      option?.disabled && "pointer-events-none opacity-50"
                    )}
                    onClick={() => !option?.disabled && handleOptionSelect(option)}
                    onMouseEnter={(e) => handleMouseEnter(option, e)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <ThumbnailPreview kv={option} size="small" className="mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{option?.kvId}</span>
                        <span className="text-muted-foreground truncate">
                          {getBrandName?.(option?.brandId)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {option?.title}
                      </p>
                    </div>
                    {value === option?.value && (
                      <Icon name="Check" size={16} className="ml-2 flex-shrink-0" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Large Preview Tooltip */}
      {hoveredOption && (
        <LargePreviewTooltip 
          kv={hoveredOption} 
          position={tooltipPosition}
        />
      )}
    </>
  );
};

export default KVSelectWithPreview;